// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import uuid from 'uuid/v4';

import { EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import type { Command } from '@atlaskit/editor-common/types';
import { type NodeType } from '@atlaskit/editor-prosemirror/model';
import type { EditorState, Selection, Transaction } from '@atlaskit/editor-prosemirror/state';
import { AllSelection, NodeSelection, TextSelection } from '@atlaskit/editor-prosemirror/state';
import { findParentNode, findParentNodeOfType } from '@atlaskit/editor-prosemirror/utils';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';
import { isFedRamp } from '@atlassian/atl-context';

import type { AIGlobalOptIn } from '../../types/types';
import { configItemUsesRovo } from '../../ui/components/SessionUI/utils/checkMigrationStatus';
import type { EditorPluginAIConfigItem } from '../../ui/prebuilt/config-items/config-items';
import {
	isConfluenceTitleToolbarSuggestTitle,
	isSuggestTitleForConfluence,
} from '../../ui/prebuilt/config-items/utils/suggest-title';
import { isAdfStreamingEnabled } from '../../ui/utils/adf-streaming';
import { getSupportedNodesForAIModal, isCellSelection } from '../../ui/utils/ai-button';
import { convertExperienceName } from '../../ui/utils/analytics/analyticsFlow';
import type {
	InvokedFrom,
	InvokedFromTriggerMethod,
} from '../../ui/utils/analytics/analyticsFlowTypes';
import { addAnalytics } from '../../ui/utils/analytics/utils';
import { type Positions } from '../../ui/utils/xstate/get-ai-experience-service';
import { endWatchingAnnotationChanges } from '../ai-annotation-watchmen/transforms';
import { type AdjustSelectionInstruction } from '../ai-event-hub/types';
import { aiSessionContextPluginKey } from '../ai-session-context/ai-session-context-plugin-key';
import { endStream } from '../in-editor-direct-streaming/transforms';
import { expandSelectionForAIExperience } from '../in-editor-direct-streaming/utils/expand-positions';
import { selectionPluginKey } from '../selection/selection-plugin-key';

import { createCommand, getPluginState } from './decoration-plugin-factory';
import type { ShowModalAction } from './reducer';

export type DecorationPositions = { endPos: number; startPos: number };

export const createWidgetDecoration =
	(isInDocumentStreamingEnabled?: boolean) =>
	(pos: number, type: 'start' | 'end', key?: string) => {
		const widgetSpanElement = document.createElement('span');
		// Firefox needs content inside the span in order to add height to the element.
		// Otherwise, the height is set to 0 which prevents the start and
		// end widgets from rendering correctly and produces incorrect measurements
		// when we need to determine where to place the modal under the content.
		// Zero width spaces have been known to introduce some strange selection
		// behaviour in the Editor, however this approach works for our use case
		// in the ai-plugin where we do not expect users to be making selections
		// over ai-highlighted content in the same way they select regular content
		// in the Editor, this is also not intended to be a permanent fix post EAP.
		if (!isInDocumentStreamingEnabled) {
			widgetSpanElement.appendChild(document.createTextNode('\u200b'));
		}
		widgetSpanElement.setAttribute('data-testid', `ai-modal-${type}-widget`);
		widgetSpanElement.className = `ai-modal-${type}-widget`;

		widgetSpanElement.contentEditable = 'false';

		return Decoration.widget(pos, widgetSpanElement, {
			side: type === 'start' ? 1 : -1,
			key: key ?? `${type}WidgetDecoration`,
		});
	};

export const createModalMountPointDecoration = (pos: number) => {
	const widgetSpanElement = document.createElement('span');
	widgetSpanElement.setAttribute('data-testid', `ai-modal-mount-point-widget`);
	widgetSpanElement.className = `ai-modal-mount-point-widget`;

	widgetSpanElement.contentEditable = 'false';

	return Decoration.widget(pos, widgetSpanElement, {
		side: 1,
		key: 'modalMountPointDecoration',
	});
};

export const createInlineDecoration = (from: number, to: number) => {
	return Decoration.inline(
		from,
		to,
		{ class: 'ai-selection-highlight', 'data-testid': 'ai-selection-highlight' },
		{ key: 'inlineDecoration' },
	);
};

export const createGeneratedContentDecorations = (
	from: number,
	to: number,
	isInDocumentStreamingEnabled?: boolean,
) => {
	const startWidgetDecoration = createWidgetDecoration(isInDocumentStreamingEnabled)(
		from,
		'start',
		'generatedStartWidget',
	);

	const highlightInlineDecoration = Decoration.inline(
		from,
		to,
		{ class: 'ai-generated-content-highlight', 'data-testid': 'ai-generated-content-highlight' },
		{ key: 'generatedInlineDecoration' },
	);

	const endWidgetDecoration = createWidgetDecoration(isInDocumentStreamingEnabled)(
		to,
		'end',
		'generatedEndWidget',
	);

	return [startWidgetDecoration, highlightInlineDecoration, endWidgetDecoration];
};

export const createNodeDecoration = (from: number, to: number) => {
	return Decoration.node(
		from,
		to,
		{ class: 'ai-selection-node', 'data-testid': 'ai-selection-node' },
		{ key: 'ai-nodeDecoration' },
	);
};

export const createGeneratedNodeDecorations = (from: number, to: number) => {
	return Decoration.node(
		from,
		to,
		{ class: 'ai-generated-node-highlight', 'data-testid': 'ai-generated-node-highlight' },
		{ key: 'generatedNodeDecoration' },
	);
};

function getInitialPositions(selection: Selection): DecorationPositions {
	switch (true) {
		case selection instanceof AllSelection:
			return { startPos: selection.from + 1, endPos: selection.to - 1 };
		default:
			return { startPos: selection.from, endPos: selection.to };
	}
}

export const getModalDecorationsForNodeSelection = ({
	state,
}: Pick<OpenAIModalProps, 'state' | 'positions'>) => {
	const { selection } = state;

	let from: number | undefined, to: number | undefined;
	if (selection instanceof NodeSelection) {
		from = selection.from;
		to = selection.to;
	} else if (isCellSelection(state)) {
		const findResult = findParentNodeOfType(state.schema.nodes.table)(selection);
		if (findResult) {
			const { depth } = findResult;
			from = selection.$from.before(depth);
			to = selection.$from.after(depth);
		}
	} else {
		const { selection } = state;
		const supportedNodes = getSupportedNodesForAIModal(state);

		const findResult = findParentNode((node) => supportedNodes.has(node.type))(selection);
		if (findResult) {
			const { depth } = findResult;
			from = selection.$from.before(depth);
			to = selection.$from.after(depth);
		}
	}

	if (from !== undefined && to !== undefined) {
		return [createNodeDecoration(from, to)];
	}
	return [];
};

export const getModalDecorationsForRangeSelection = ({
	state,
	configItem,
	lastTriggeredFrom,
	initialPositions,
}: Omit<OpenAIModalProps, 'triggerMethod' | 'aiGlobalOptIn' | 'initialPrompt' | 'triggeredFor'> & {
	initialPositions: Positions;
}) => {
	/**
	 * We add selection highlight decoration based on the current selection state of the document and the config item in the command palette
	 * If the selection is empty and the document is not empty -> we decorate the entire document unless the config item specifies otherwise
	 */
	let useCurrentSelectionForHighlight = true;

	// when clicking `browse agents` then selecting an agent, the state.selection is already cleared
	// so we need to use initialPositions to check if the selection is empty
	if (initialPositions[0] === initialPositions[1] && state.doc.content.size !== 0) {
		useCurrentSelectionForHighlight = !!configItem.doNotHighlightDocWhenSelectionIsEmpty;
	}

	/**
	 * EXPERIMENTAL: For experimental flow where 'Suggest title' in
	 * Confluence title toolbar will trigger the 'Suggest a title' flow in Editor AI.
	 * Forces whole document selection, even if an existing selection exists.
	 */
	if (isConfluenceTitleToolbarSuggestTitle({ configItem, lastTriggeredFrom })) {
		useCurrentSelectionForHighlight = false;
	}

	const { startPos, endPos } = useCurrentSelectionForHighlight
		? { startPos: initialPositions[0], endPos: initialPositions[1] }
		: getInitialPositions(new AllSelection(state.doc));

	let decorations: Decoration[] = [];
	const { isInDocumentStreamingEnabled } = getPluginState(state);

	const startWidgetDecoration = createWidgetDecoration(isInDocumentStreamingEnabled)(
		initialPositions[0],
		'start',
	);
	const endWidgetDecoration = createWidgetDecoration(isInDocumentStreamingEnabled)(
		initialPositions[1],
		'end',
	);
	decorations =
		initialPositions[0] === initialPositions[1]
			? [endWidgetDecoration]
			: [startWidgetDecoration, endWidgetDecoration];
	decorations.push(createInlineDecoration(startPos, endPos));

	return decorations;
};

function createEmptySelectionDecoration(startPos: number) {
	const widgetSpanElement = document.createElement('span');
	widgetSpanElement.setAttribute('data-testid', 'ai-pre-stream-empty-selection-caret');
	widgetSpanElement.className = 'ai-pre-stream-empty-selection-caret';

	widgetSpanElement.contentEditable = 'false';
	return Decoration.widget(startPos, widgetSpanElement, {
		side: 1,
		key: 'ai-pre-stream-empty-selection-caret',
	});
}

export const getModalDecorations = ({
	state,
	configItem,
	initialPositions,
}: {
	configItem: EditorPluginAIConfigItem;
	initialPositions: Positions;
	state: EditorState;
}) => {
	// We add selection highlight decoration based on the current selection of the document
	// when clicking `browse agents` then selecting an agent, the state.selection is cleared
	// so we need to use initialPositions to get the selection positions
	const startPos = initialPositions[0];
	const endPos = initialPositions[1];
	const isEmptySelection = startPos === endPos;
	const isSuggestTitle = isSuggestTitleForConfluence(configItem);
	const needSelectionDecoration = !isSuggestTitle;

	const decorations: Decoration[] = [];
	if (needSelectionDecoration) {
		if (isEmptySelection) {
			decorations.push(createEmptySelectionDecoration(startPos));
		} else {
			const preStreamRangeDecorations = Decoration.inline(
				startPos,
				endPos,
				{
					class: 'ai-pre-stream-range-selection',
					'data-testid': 'ai-pre-stream-range-selection',
				},
				{ key: 'ai-pre-stream-range-selection' },
			);

			const mountPointRangeDecorations = Decoration.inline(
				startPos,
				endPos,
				{},
				{ key: 'modalMountPointRangeDecoration' },
			);

			decorations.push(preStreamRangeDecorations, mountPointRangeDecorations);
		}
	}

	/**
	 * Set the mount point of the widget to be directly after the endPos so that
	 * replace steps during streaming will not remove the decoration as
	 * that would unmount the modal + state machine
	 */
	const mountPos = isSuggestTitle
		? 1
		: isEmptySelection && !configItem.insertOnEmptySelection
			? state.doc.content.size
			: Math.min(endPos, state.doc.content.size);

	const mountPointDecoration = createModalMountPointDecoration(mountPos);
	decorations.push(mountPointDecoration);
	return decorations;
};

type OpenAIModalProps = {
	adjustSelection?: AdjustSelectionInstruction;
	aiGlobalOptIn: AIGlobalOptIn;
	configItem: EditorPluginAIConfigItem;
	/**
	 * Option to hide the loading screen
	 */
	hideLoadingScreen?: boolean;
	initialPrompt?: string;
	isExternal?: boolean;
	lastTriggeredFrom?: InvokedFrom;
	/**
	 * This is required for expanding selection for AI experience.
	 * Currently it is optional because making it mandatory will require
	 * 	big changes across many files and change in public api as well.
	 * So we will currently fallback to "en-GB" if it is not provided.
	 */
	locale?: string;
	nudgeMetrics?: Record<string, number | undefined>;
	positions?: DecorationPositions;
	state: EditorState;
	triggeredFor?: NodeType;
	triggerMethod?: InvokedFromTriggerMethod;
	withAcceptReject?: boolean;
	withStreamingHighlight?: boolean;
};

export const createOpenAIModalCommand = ({
	state,
	configItem,
	lastTriggeredFrom,
	triggerMethod,
	aiGlobalOptIn,
	initialPrompt,
	triggeredFor,
	/**
	 * Nudge metrics will be used passed in analytics events.
	 * It includes detailed metrics for improve writing nudge.
	 * It is currently only set when "Improve writing" is triggered from the
	 * "Improve writing" nudge.
	 */
	nudgeMetrics,
	locale,
	isExternal = false,
	withAcceptReject = true,
	withStreamingHighlight = true,
	hideLoadingScreen = false,
}: OpenAIModalProps): Command => {
	/**
	 * WARNING: Do not remove the isFedRamp check.
	 * This is to ensure that AI functionality is not enabled in FedRamp environments.
	 */
	if (aiGlobalOptIn.status === 'disabled' || isFedRamp()) {
		/**
		 * If the user has opted out of AI, or if user is in FedRamp environment,
		 * do not show the modal it's unclear how this function would be triggered as
		 * all entry points should be removed already
		 */
		return () => false;
	}

	if (aiGlobalOptIn.status === 'disabled-opt-in') {
		aiGlobalOptIn.triggerOptInFlow();

		return createCommand({}, (tr: Transaction) => {
			addAnalytics({
				editorState: state,
				tr,
				payload: {
					action: 'triggerOptInFlow',
					actionSubject: 'editorPluginAI',
					actionSubjectId: 'aiGlobalOptIn',
					attributes: {
						experienceType: convertExperienceName(configItem.key),
						lastTriggeredFrom,
						selectionType: state.selection.empty ? 'empty' : 'range',
					},
					eventType: EVENT_TYPE.UI,
				},
			});
			return tr;
		});
	}

	const { from, to } = state.selection;
	const aiDecorationExperiencePluginState = getPluginState(state);
	const decorationPluginStateInitialPositions = aiDecorationExperiencePluginState.initialPositions;

	// For external commands respect the current selection
	let initialPositions: Positions =
		isExternal && aiDecorationExperiencePluginState.isInDocumentStreamingEnabled
			? [from, to]
			: decorationPluginStateInitialPositions || [from, to];
	const adfStreamingEnabled = configItem.adfPrompt && isAdfStreamingEnabled();

	// No need to expand selection for block nodes
	if (
		!triggeredFor?.isBlock &&
		(adfStreamingEnabled || aiDecorationExperiencePluginState.isInDocumentStreamingEnabled)
	) {
		// For external commands respect the current selection
		if (isExternal) {
			const [start, end] = expandSelectionForAIExperience({
				selectionPositions: { startPos: state.selection.$from, endPos: state.selection.$to },
				/**
				 * Currently locale is optional because making it mandatory will require
				 * 	big changes across many files and change in public api as well.
				 * So we will currently fallback to "en-GB" if it is not provided.
				 */
				context: {
					locale: locale ?? 'en-GB',
					selection: state.selection,
				},
				options: {
					shouldExpandSelectionToCompleteWords: true,
					shouldExpandSelectionToMediaIfInCaption: true,
					shouldExpandSelectionToNearestBlockNode: true,
					shouldTransformCellSelectionToTextRange: true,
				},
			});
			initialPositions = [start, end];
		} else {
			const selectionPositions = decorationPluginStateInitialPositions
				? {
						startPos: state.doc.resolve(decorationPluginStateInitialPositions[0]),
						endPos: state.doc.resolve(decorationPluginStateInitialPositions[1]),
					}
				: { startPos: state.selection.$from, endPos: state.selection.$to };
			const [start, end] = expandSelectionForAIExperience({
				selectionPositions,
				/**
				 * Currently locale is optional because making it mandatory will require
				 * 	big changes across many files and change in public api as well.
				 * So we will currently fallback to "en-GB" if it is not provided.
				 */
				context: {
					locale: locale ?? 'en-GB',
					selection: state.selection,
				},
				options: {
					shouldExpandSelectionToCompleteWords: true,
					shouldExpandSelectionToMediaIfInCaption: true,
					shouldExpandSelectionToNearestBlockNode: true,
					shouldTransformCellSelectionToTextRange: true,
					shouldExpandSelectionToEntireLayout:
						aiDecorationExperiencePluginState.isInDocumentStreamingEnabled,
				},
			});
			initialPositions = [start, end];
		}
	}

	let modalDecorationSet = DecorationSet.empty;
	if (triggeredFor?.isBlock) {
		// First get the positions that the decorations will be added to the document at
		const decorations = getModalDecorationsForNodeSelection({ state });
		if (decorations.length) {
			modalDecorationSet = modalDecorationSet.add(state.doc, decorations);
		}
	} else {
		const decorations = aiDecorationExperiencePluginState.isInDocumentStreamingEnabled
			? getModalDecorations({ state, configItem, initialPositions })
			: getModalDecorationsForRangeSelection({
					state,
					configItem,
					lastTriggeredFrom,
					initialPositions,
				});
		if (decorations.length) {
			modalDecorationSet = modalDecorationSet.add(state.doc, decorations);
		}
	}

	// This "command" is later picked up by a state reducer
	const action: ShowModalAction = {
		type: 'SHOW_MODAL',
		data: {
			// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
			interactionSessionId: uuid(),
			lastTriggeredFrom,
			triggerMethod,
			modalDecorationSet,
			configItem,
			modalMountedTimeStamp: Date.now(),
			initialPrompt,
			triggeredFor,
			initialPositions: initialPositions,
			initialSelection: aiDecorationExperiencePluginState.initialSelection || state.selection,
			nudgeMetrics,
			withAcceptReject,
			withStreamingHighlight,
			hideLoadingScreen,
		},
	};

	if (aiDecorationExperiencePluginState.isInDocumentStreamingEnabled) {
		return createCommand(action, (tr: Transaction) => {
			// This is a special use case where we want to clear the selection locally, but inform the collab users that a selection
			// range was selected. The useSelectionBookmarkForRemote will be sent to collab users instead of the local setSelection.
			// We don't want to have a selection range in the editor when we start replacing the content with AI generated content.
			// This is because the FocusLock will keep draging focus away which will break selection ranges.
			// See: https://hello.jira.atlassian.cloud/browse/EDITOR-1090 for more details.
			const selection = tr.selection;
			const { to, from } = selection;
			if (!triggeredFor?.isBlock && to !== from) {
				tr.setSelection(TextSelection.create(tr.doc, to));
				// There will be weird selection highlight if we don't clear the selection manually
				// see https://hello.jira.atlassian.cloud/browse/EDITOR-1405
				const domSelection = window.getSelection();
				if (domSelection) {
					domSelection.removeAllRanges();
				}
			}

			if (
				!isExternal &&
				configItemUsesRovo(configItem.key) &&
				fg('platform_editor_ai_migrate_from_xstate')
			) {
				tr.setMeta(aiSessionContextPluginKey, {
					type: 'PROMPT',
				});
			}
			// when ai orchestrator endStreamCommand timeout, we need endStream to reset streaming state
			return endStream(tr)
				.setMeta(
					'useSelectionBookmarkForRemote',
					TextSelection.between(
						tr.doc.resolve(initialPositions[0]),
						tr.doc.resolve(initialPositions[1]),
					).getBookmark(),
				)
				.setMeta(selectionPluginKey, {
					action: 'lastSelectionContext',
					selection,
				})
				.setMeta('scrollIntoView', false);
		});
	} else {
		// Clear the selection after opening the modal to hide annotation toolbar
		return createCommand(action, (tr: Transaction) => {
			const selection = tr.selection;
			const { to, from } = selection;
			if (!triggeredFor?.isBlock && to !== from) {
				tr.setSelection(TextSelection.create(tr.doc, to));
			}
			return tr;
		});
	}
};

export const createEndAIExperienceCommand = (
	modalDecorationSet?: DecorationSet,
	preserveEditorSelectionOnComplete?: boolean,
	shouldClearSelectionContext?: boolean,
	shouldIgnoreNextSpaceTrigger?: boolean,
): Command => {
	return createCommand(
		(state: EditorState) => {
			if (modalDecorationSet && !preserveEditorSelectionOnComplete) {
				return {
					type: 'END_EXPERIENCE',
					data: {
						modalDecorationSet,
						ignoredSpaceTriggerSelection: shouldIgnoreNextSpaceTrigger
							? state.selection
							: undefined,
					},
				};
			}
			return {
				type: 'END_EXPERIENCE',
				data: {
					ignoredSpaceTriggerSelection: shouldIgnoreNextSpaceTrigger ? state.selection : undefined,
				},
			};
		},
		(tr: Transaction, state: EditorState) => {
			if (preserveEditorSelectionOnComplete) {
				const inlineDeco = modalDecorationSet?.find(
					undefined,
					undefined,
					(spec) => spec.key === 'inlineDecoration',
				)?.[0];
				if (inlineDeco) {
					tr.setSelection(
						TextSelection.create(
							tr.doc,
							inlineDeco.from,
							Math.min(inlineDeco.to, tr.doc.content.size),
						),
					);
				}
			}

			const aiDecorationExperiencePluginState = getPluginState(state);
			if (aiDecorationExperiencePluginState.isInDocumentStreamingEnabled) {
				if (shouldClearSelectionContext === false) {
					tr.setMeta(selectionPluginKey, {
						action: 'lastSelectionContextPending',
					});
				} else {
					tr.setMeta(selectionPluginKey, {
						action: 'lastSelectionContext',
						selection: undefined,
					});
				}

				return endStream(endWatchingAnnotationChanges(tr));
			}

			return tr;
		},
	);
};

export const clearSelectionDecorations = (state: EditorState): Command => {
	const aiDecorationExperiencePluginState = getPluginState(state);
	const modalDecorationSet = aiDecorationExperiencePluginState.modalDecorationSet;
	const selectionDecorations = modalDecorationSet.find(
		undefined,
		undefined,
		(spec) =>
			spec.key === 'ai-pre-stream-range-selection' ||
			spec.key === 'ai-pre-stream-empty-selection-caret',
	);

	return createCommand({
		type: 'UPDATE_MODAL_DECORATION_SET',
		data: { modalDecorationSet: modalDecorationSet.remove(selectionDecorations) },
	});
};

export const createUpdateEndWidgetDecorationPositionCommand = (
	state: EditorState,
	endWidgetDecorationPosition: number,
): Command => {
	const aiDecorationExperiencePluginState = getPluginState(state);
	const modalDecorationSet = aiDecorationExperiencePluginState.modalDecorationSet;
	const decorationWidget = modalDecorationSet.find(
		undefined,
		undefined,
		(spec) => spec.key === 'endWidgetDecoration',
	);
	let updated = modalDecorationSet.remove(decorationWidget);
	const updatedEndWidgetDecoration = createWidgetDecoration(
		aiDecorationExperiencePluginState.isInDocumentStreamingEnabled,
	)(endWidgetDecorationPosition, 'end');
	updated = updated.add(state.doc, [updatedEndWidgetDecoration]);
	return createCommand({
		type: 'UPDATE_MODAL_DECORATION_SET',
		data: { modalDecorationSet: updated },
	});
};

export const createStartAISessionCommand = (sessionId: string, traceId?: string): Command => {
	return createCommand({
		type: 'START_SESSION',
		data: {
			sessionId,
			traceId: fg('platform_editor_ai_traceId_in_analytics_events') ? traceId : undefined,
		},
	});
};

export const createEndAISessionCommand = (sessionId: string): Command => {
	return createCommand((state) => {
		const aiDecorationExperiencePluginState = getPluginState(state);
		const nextSessionId = aiDecorationExperiencePluginState.nextSessionId;

		// If a sessions which is not the next session is trying to be ended then we should abort trying to end the session.
		if (sessionId !== nextSessionId) {
			return false;
		}

		return {
			type: 'END_SESSION',
			data: { sessionId: undefined },
		};
	});
};

export const createUpdateTargetIdCommand = (targetId: string | number): Command => {
	return createCommand({
		type: 'UPDATE_TARGET_ID',
		data: { targetId },
	});
};
